import React, { useState, useRef, useEffect } from "react";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import logger from "src/utils/logger";
import { Button } from "src/components/ui/Button";

const TestButtonPage = () => {
  const [imagePreview, setImagePreview] = useState(null);
  const [results, setResults] = useState(null);
  const [faceActions, setFaceActions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [faceLandmarker, setFaceLandmarker] = useState(null);
  const [isModelLoading, setIsModelLoading] = useState(true);
  const imageRef = useRef(null);

  useEffect(() => {
    const initializeFaceLandmarker = async () => {
      try {
        const resolver = await FilesetResolver.forVisionTasks(
          "/mediapipe/tasks-vision/wasm"
        );

        const faceLandmarkerOptions = {
          baseOptions: {
            modelAssetPath: "/model/face_landmarker.task",
            delegate: "CPU",
          },
          outputFaceBlendshapes: true,
          runningMode: "IMAGE",
          numFaces: 1,
        };

        const faceMarker = await FaceLandmarker.createFromOptions(
          resolver,
          faceLandmarkerOptions
        );
        setFaceLandmarker(faceMarker);
        setIsModelLoading(false);
        logger.log("FaceLandmarker initialized for IMAGE mode");
      } catch (error) {
        logger.error("Error initializing face landmarker:", error);
        setIsModelLoading(false);
      }
    };

    initializeFaceLandmarker();

    return () => {
      if (faceLandmarker) {
        faceLandmarker.close();
      }
    };
  }, []);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setResults(null);
      setFaceActions(null);
    }
  };

  const validateFaceQuality = (landmarks) => {
    const leftCheek = landmarks[234];
    const rightCheek = landmarks[454];
    const chin = landmarks[152];
    const forehead = landmarks[10];
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    
    const faceWidth = Math.abs(rightCheek.x - leftCheek.x);
    const faceHeight = Math.abs(chin.y - forehead.y);
    const eyeDistance = Math.abs(rightEye.x - leftEye.x);
    
    if (faceWidth < 0.15 || faceHeight < 0.20) {
      return { valid: false, reason: 'face_too_small', faceWidth, faceHeight, eyeDistance };
    }
    
    const allLandmarksVisible = 
      leftCheek.x > 0.05 && rightCheek.x < 0.95 &&
      forehead.y > 0.02 && chin.y < 0.98;
    
    if (!allLandmarksVisible) {
      return { valid: false, reason: 'face_partially_visible', faceWidth, faceHeight, eyeDistance, leftCheek: leftCheek.x, rightCheek: rightCheek.x, forehead: forehead.y, chin: chin.y };
    }
    
    if (eyeDistance < 0.06) {
      return { valid: false, reason: 'face_too_rotated', faceWidth, faceHeight, eyeDistance };
    }
    
    return { valid: true, reason: null, faceWidth, faceHeight, eyeDistance };
  };

  const analyzeImage = async () => {
    if (!imageRef.current || !faceLandmarker) return;

    setIsLoading(true);
    try {
      const result = faceLandmarker.detect(imageRef.current);
      setResults(result);

      if (result.faceBlendshapes && result.faceBlendshapes.length > 0) {
        const categories = result.faceBlendshapes[0].categories;
        const getCategoryScore = (categoryName) => {
          const category = categories.find(c => c.categoryName === categoryName);
          return category ? category.score : 0;
        };

        const threshold = 0.75;
        const actions = {
          right: false,
          left: false,
          up: false,
          down: false,
          smile: false,
          rightblink: false,
          leftblink: false,
          faceQuality: null,
          scores: {
            eyeLookOutLeft: getCategoryScore('eyeLookOutLeft'),
            eyeLookOutRight: getCategoryScore('eyeLookOutRight'),
            eyeLookUpLeft: getCategoryScore('eyeLookUpLeft'),
            eyeLookUpRight: getCategoryScore('eyeLookUpRight'),
            eyeLookDownLeft: getCategoryScore('eyeLookDownLeft'),
            eyeLookDownRight: getCategoryScore('eyeLookDownRight'),
            mouthSmileLeft: getCategoryScore('mouthSmileLeft'),
            mouthSmileRight: getCategoryScore('mouthSmileRight'),
            eyeBlinkLeft: getCategoryScore('eyeBlinkLeft'),
            eyeBlinkRight: getCategoryScore('eyeBlinkRight'),
          }
        };

        if (result.faceLandmarks && result.faceLandmarks.length > 0) {
          const landmarks = result.faceLandmarks[0];
          
          const qualityCheck = validateFaceQuality(landmarks);
          actions.faceQuality = qualityCheck;

          const noseTip = landmarks[1];
          const leftEyeInnerCorner = landmarks[133];
          const rightEyeInnerCorner = landmarks[362];

          const xCenter = (leftEyeInnerCorner.x + rightEyeInnerCorner.x) / 2;
          const xDiff = noseTip.x - xCenter;

          actions.headPosition = {
            noseTip: { x: noseTip.x, y: noseTip.y },
            xCenter,
            xDiff,
          };

          if (qualityCheck.valid) {
            actions.down = getCategoryScore('eyeLookUpLeft') > 0.6 && getCategoryScore('eyeLookUpRight') > 0.6;
            actions.up = getCategoryScore('eyeLookDownLeft') > 0.6 && getCategoryScore('eyeLookDownRight') > 0.6;
            actions.smile = getCategoryScore('mouthSmileLeft') > threshold && getCategoryScore('mouthSmileRight') > threshold;
            actions.rightblink = getCategoryScore('eyeBlinkRight') > 0.5 && getCategoryScore('eyeBlinkLeft') < 0.4;
            actions.leftblink = getCategoryScore('eyeBlinkLeft') > 0.5 && getCategoryScore('eyeBlinkRight') < 0.4;

            if (xDiff < -0.04) {
              actions.right = true;
            } else if (xDiff > 0.04) {
              actions.left = true;
            }
          }
        } else {
          actions.faceQuality = { valid: false, reason: 'no_landmarks' };
        }

        setFaceActions(actions);
      } else {
        setFaceActions({ faceQuality: { valid: false, reason: 'no_face_detected' } });
      }
    } catch (error) {
      logger.error("Error analyzing image:", error);
    }
    setIsLoading(false);
  };

  const getActionStatus = (isActive) => (
    <span className={`px-2 py-1 rounded text-sm ${isActive ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
      {isActive ? '✓ DETECTED' : '✗ NOT DETECTED'}
    </span>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Face Action Test Page</h1>
      
      {isModelLoading ? (
        <div className="flex items-center gap-2 mb-4 p-4 bg-yellow-100 rounded">
          <div className="inline-block h-5 w-5 border-2 border-t-transparent border-yellow-600 rounded-full animate-spin"></div>
          <span>Loading FaceLandmarker model...</span>
        </div>
      ) : (
        <div className="mb-4 p-4 bg-green-100 rounded text-green-700">
          ✓ FaceLandmarker model ready
        </div>
      )}

      <div className="mb-6">
        <label className="block mb-2 font-medium">Upload Image:</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
      </div>

      {imagePreview && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Preview:</h2>
          <img
            ref={imageRef}
            src={imagePreview}
            alt="Preview"
            className="max-w-md rounded border"
            onLoad={() => logger.log("Image loaded")}
          />
          <Button
            type="primary"
            htmlType="button"
            onClick={analyzeImage}
            loading={isLoading}
            disabled={isLoading || isModelLoading}
            className="mt-4"
            variant=""
          >
            {isLoading ? "Analyzing..." : "Analyze Image"}
          </Button>
        </div>
      )}

      {faceActions && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <h2 className="text-lg font-semibold mb-4">Face Quality Check:</h2>
          {faceActions.faceQuality && (
            <div className={`p-4 rounded mb-4 ${faceActions.faceQuality.valid ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-lg font-bold ${faceActions.faceQuality.valid ? 'text-green-700' : 'text-red-700'}`}>
                  {faceActions.faceQuality.valid ? '✓ FACE QUALITY OK' : '✗ FACE QUALITY FAILED'}
                </span>
              </div>
              {!faceActions.faceQuality.valid && (
                <p className="text-red-600 font-medium">
                  Reason: {faceActions.faceQuality.reason === 'face_too_small' && 'Yüz çok küçük (uzaktan çekilmiş)'}
                  {faceActions.faceQuality.reason === 'face_partially_visible' && 'Yüzün bir kısmı görünmüyor'}
                  {faceActions.faceQuality.reason === 'face_too_rotated' && 'Yüz çok fazla dönük (profil)'}
                  {faceActions.faceQuality.reason === 'face_not_frontal' && 'Yüz kameraya dönük değil'}
                  {faceActions.faceQuality.reason === 'face_asymmetric' && 'Yüz asimetrik (yan dönük)'}
                  {faceActions.faceQuality.reason === 'face_distorted' && 'Yüz oranları bozuk'}
                  {faceActions.faceQuality.reason === 'face_tilted' && 'Yüz eğik (yukarı/aşağı)'}
                  {faceActions.faceQuality.reason === 'no_landmarks' && 'Yüz landmark\'ları tespit edilemedi'}
                  {faceActions.faceQuality.reason === 'no_face_detected' && 'Yüz tespit edilemedi'}
                </p>
              )}
            </div>
          )}

          <h2 className="text-lg font-semibold mb-4">Face Actions Detected:</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Smile:</span> {getActionStatus(faceActions.smile)}
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Look Right:</span> {getActionStatus(faceActions.right)}
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Look Left:</span> {getActionStatus(faceActions.left)}
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Look Up:</span> {getActionStatus(faceActions.up)}
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Look Down:</span> {getActionStatus(faceActions.down)}
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Right Eye Blink:</span> {getActionStatus(faceActions.rightblink)}
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Left Eye Blink:</span> {getActionStatus(faceActions.leftblink)}
            </div>
          </div>

          {faceActions.scores && (
            <>
              <h3 className="text-md font-semibold mt-6 mb-3">Raw Scores:</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(faceActions.scores).map(([key, value]) => (
                  <div key={key} className="flex justify-between p-2 bg-white rounded">
                    <span className="text-gray-600">{key}:</span>
                    <span className="font-mono">{value.toFixed(4)}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {faceActions.headPosition && (
            <>
              <h3 className="text-md font-semibold mt-6 mb-3">Head Position:</h3>
              <div className="p-2 bg-white rounded text-sm font-mono">
                <p>Nose X: {faceActions.headPosition.noseTip.x.toFixed(4)}</p>
                <p>X Center: {faceActions.headPosition.xCenter.toFixed(4)}</p>
                <p>X Diff: {faceActions.headPosition.xDiff.toFixed(4)}</p>
              </div>
            </>
          )}
        </div>
      )}

      {results && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Full Results (JSON):</h2>
          <details className="p-4 bg-gray-900 text-green-400 rounded overflow-auto">
            <summary className="cursor-pointer mb-2">Click to expand</summary>
            <pre className="text-xs max-h-96 overflow-auto">
              {JSON.stringify(results, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default TestButtonPage;